import { isFilled, validateWebhookUrl, WebhookBodyFormat } from "lopermut"
import z from "zod"
import { Errors, validatePhoneNumber } from ".."
import { Channel, ChannelEnum } from "../campaigns/commonTypes"

// Field types shared across capability configurations
const JsonSchemaSimpleType = {
  string: "string",
  number: "number",
  boolean: "boolean",
} as const

export const JsonSchemaTypeSchema = z
  .union([
    z.enum(JsonSchemaSimpleType),
    z.object({
      kind: z.literal("enum"),
      values: z
        .array(z.string().min(1, { error: Errors.FIELD_REQUIRED }))
        .min(1, { error: Errors.FIELD_REQUIRED }),
    }),
  ])
  .default("string")

const organizationPhoneNumberRefine = ({
  organizationPhoneNumbers,
}: {
  organizationPhoneNumbers?: string | null
}) => {
  const numbers = organizationPhoneNumbers?.split("\n") ?? []

  for (const number of numbers) {
    if (!validatePhoneNumber(number)) {
      return false
    }
  }

  return true
}

// Support capability configuration
export const SupportHandoffPolicy = {
  DeflectGracefully: "DeflectGracefully",
  EscalateToSupport: "EscalateToSupport",
  ShareContactDetails: "ShareContactDetails",
} as const

export const SupportEscalationStrategy = {
  OneTicketPerIssue: "OneTicketPerIssue",
  OneActiveTicketPerCustomer: "OneActiveTicketPerCustomer",
} as const

export const ContactChannel = {
  PhoneOrEmail: "phoneOrEmail",
  Phone: "phone",
  Email: "email",
  None: "none",
} as const

export const ContactAdditionalFieldSchema = z.object({
  label: z.string().min(1, { message: Errors.FIELD_REQUIRED }),
  required: z.boolean().default(true),
})

export type ContactAdditionalField = z.infer<typeof ContactAdditionalFieldSchema>

const contactRequirementShape = {
  contactChannel: z.enum(ContactChannel).default(ContactChannel.PhoneOrEmail),
  additionalFields: z.array(ContactAdditionalFieldSchema).default([]),
}

const refineContactRequirement = (
  data: {
    contactChannel: (typeof ContactChannel)[keyof typeof ContactChannel]
    additionalFields: ContactAdditionalField[]
  },
  ctx: z.RefinementCtx,
) => {
  if (data.contactChannel === ContactChannel.None && data.additionalFields.length > 0) {
    ctx.addIssue({
      code: "custom",
      message: Errors.CAPABILITY_CONFIG_INVALID,
      path: ["additionalFields"],
    })
  }
}

export const SupportReplyChannel = {
  Conversation: "conversation",
  Email: "email",
} as const

export const SupportReplyPolicySchema = z
  .object({
    primary: z.enum(SupportReplyChannel),
    alternative: z.enum(SupportReplyChannel).optional(),
  })
  .superRefine((policy, ctx) => {
    if (policy.primary === policy.alternative) {
      ctx.addIssue({
        code: "custom",
        message: Errors.CAPABILITY_CONFIG_INVALID,
        path: ["alternative"],
      })
    }
  })

export const SupportCapabilityConfigSchema = z
  .object({
    handoffPolicy: z.enum(SupportHandoffPolicy).default(SupportHandoffPolicy.DeflectGracefully),
    escalationStrategy: z
      .enum(SupportEscalationStrategy)
      .default(SupportEscalationStrategy.OneTicketPerIssue),
    supportPhone: z.string().optional(),
    supportEmail: z.string().optional(),
    replyPolicyByChannel: z
      .partialRecord(z.enum(ChannelEnum).exclude([ChannelEnum.Email]), SupportReplyPolicySchema)
      .default({}),
    ...contactRequirementShape,
  })
  .superRefine((data, ctx) => {
    if (
      data.handoffPolicy === SupportHandoffPolicy.ShareContactDetails &&
      !isFilled(data.supportPhone) &&
      !isFilled(data.supportEmail)
    ) {
      ctx.addIssue({
        code: "custom",
        message: Errors.FIELD_REQUIRED,
        path: ["supportPhone"],
      })
      ctx.addIssue({
        code: "custom",
        message: Errors.FIELD_REQUIRED,
        path: ["supportEmail"],
      })
    }

    refineContactRequirement(data, ctx)
  })

export type SupportCapabilityConfig = z.infer<typeof SupportCapabilityConfigSchema>

const DEFAULT_SUPPORT_REPLY_POLICY: z.infer<typeof SupportReplyPolicySchema> = {
  primary: SupportReplyChannel.Email,
}

export const getSupportReplyPolicy = (
  config: Pick<SupportCapabilityConfig, "replyPolicyByChannel">,
  channel: Channel | null,
) => {
  if (!channel || channel === ChannelEnum.Email) return DEFAULT_SUPPORT_REPLY_POLICY

  return config.replyPolicyByChannel[channel] ?? DEFAULT_SUPPORT_REPLY_POLICY
}

// React to message configuration
export const ReactToMessageCapabilityConfigSchema = z.object({
  emojis: z
    .string()
    .transform((v) => (isFilled(v) ? v : "❤️"))
    .default("❤️"),
  definition: z.string().max(1024, { message: Errors.FIELD_MAX_LENGTH }).default(""),
})

export type ReactToMessageCapabilityConfig = z.infer<typeof ReactToMessageCapabilityConfigSchema>

// Business opportunity configuration
export const BusinessOpportunityCapabilityConfigSchema = z
  .object({
    definition: z.string().max(1536, { message: Errors.FIELD_MAX_LENGTH }).default(""),
    requiredFields: z
      .array(
        z.object({
          key: z.string().min(1, { message: Errors.FIELD_REQUIRED }),
          description: z.string().min(1, { message: Errors.FIELD_REQUIRED }),
          type: JsonSchemaTypeSchema,
          required: z.boolean().default(true),
        }),
      )
      .default([]),
    ...contactRequirementShape,
  })
  .superRefine(refineContactRequirement)

export type BusinessOpportunityCapabilityConfig = z.infer<
  typeof BusinessOpportunityCapabilityConfigSchema
>

// Find order number configuration
export const FindOrderNumberCapabilityConfigSchema = z
  .object({
    provider: z.literal("Shopify").default("Shopify"),
    organizationPhoneNumbers: z.string().nullish(),
  })
  .refine(organizationPhoneNumberRefine, {
    message: Errors.INVALID_PHONE_NUMBER,
    path: ["organizationPhoneNumbers"],
  })

// Get order tracking configuration
export const GetOrderTrackingProvider = {
  Shopify: "Shopify",
  BigBlue: "BigBlue",
  Amphora: "Amphora",
} as const

export const OrderTrackingCapabilityConfigSchema = z
  .object({
    provider: z.enum(GetOrderTrackingProvider),
    organizationPhoneNumbers: z.string().nullish(),
    activationInstructions: z.string().max(2048, { message: Errors.FIELD_MAX_LENGTH }).default(""),
    responseInstructions: z.string().max(2048, { message: Errors.FIELD_MAX_LENGTH }).default(""),
  })
  .refine(organizationPhoneNumberRefine, {
    message: Errors.INVALID_PHONE_NUMBER,
    path: ["organizationPhoneNumbers"],
  })

// Get order refund status configuration
export const GetOrderRefundStatusProvider = {
  Reveni: "Reveni",
  LoopReturns: "LoopReturns",
  Rever: "Rever",
} as const

export const GetOrderRefundStatusCapabilityConfigSchema = z
  .object({
    provider: z.enum(GetOrderRefundStatusProvider).optional(),
    organizationPhoneNumbers: z.string().nullish(),
  })
  .refine(organizationPhoneNumberRefine, {
    message: Errors.INVALID_PHONE_NUMBER,
    path: ["organizationPhoneNumbers"],
  })

export const CancelOrderCapabilityConfigSchema = z
  .object({
    shouldAutoRefund: z.boolean().default(true),
    shouldRestock: z.boolean().default(true),
    cancellationWindowMinutes: z.number().int().positive().nullish(),
    organizationPhoneNumbers: z.string().nullish(),
    activationInstructions: z.string().max(2048, { message: Errors.FIELD_MAX_LENGTH }).default(""),
    responseInstructions: z.string().max(2048, { message: Errors.FIELD_MAX_LENGTH }).default(""),
  })
  .refine(organizationPhoneNumberRefine, {
    message: Errors.INVALID_PHONE_NUMBER,
    path: ["organizationPhoneNumbers"],
  })

// Update order configuration
export const UpdateOrderProvider = {
  Shopify: "Shopify",
  Amphora: "Amphora",
  BigBlue: "BigBlue",
} as const

export const UpdateOrderCapabilityConfigSchema = z
  .object({
    provider: z.enum(UpdateOrderProvider),
    activationInstructions: z.string().max(2048, { message: Errors.FIELD_MAX_LENGTH }).default(""),
    responseInstructions: z.string().max(2048, { message: Errors.FIELD_MAX_LENGTH }).default(""),
    isShippingAddressUpdateEnabled: z.boolean().default(true),
    isRecipientNameUpdateEnabled: z.boolean().default(true),
    isOnHoldEnabled: z.boolean().optional(),
    isDeliveryPriorityUpdateEnabled: z.boolean().default(true),
    isProductUpdateEnabled: z.boolean().default(false),
  })
  .transform((data) => ({
    ...data,
    isOnHoldEnabled: data.isOnHoldEnabled ?? data.provider !== UpdateOrderProvider.BigBlue,
  }))
  .refine(
    (data) =>
      data.isShippingAddressUpdateEnabled ||
      data.isRecipientNameUpdateEnabled ||
      data.isOnHoldEnabled ||
      data.isDeliveryPriorityUpdateEnabled ||
      data.isProductUpdateEnabled,
    { message: Errors.CAPABILITY_CONFIG_INVALID },
  )
  .superRefine((data, context) => {
    if (data.provider === UpdateOrderProvider.BigBlue && data.isOnHoldEnabled) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: Errors.CAPABILITY_CONFIG_INVALID,
        path: ["isOnHoldEnabled"],
      })
    }

    if (
      data.provider === UpdateOrderProvider.Shopify &&
      (data.isOnHoldEnabled || data.isDeliveryPriorityUpdateEnabled)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: Errors.CAPABILITY_CONFIG_INVALID,
        path: ["provider"],
      })
    }
  })

export type UpdateOrderCapabilityConfig = z.infer<typeof UpdateOrderCapabilityConfigSchema>

// Search catalog configuration
export const SearchCatalogProvider = {
  Shopify: "Shopify",
} as const

export const SearchCatalogUtmParamsSchema = z.object({
  source: z.string().refine((value) => value === "permut"),
  medium: z.string().refine((value) => value === "{{ conversation.channel }}"),
  campaign: z.string().refine((value) => value === "{{ conversation.campaign.name }}"),
  term: z.string().min(1).nullish(),
  content: z.string().min(1).nullish(),
})

export type SearchCatalogUtmParams = {
  source: string
  medium: string
  campaign: string
  term?: string | null
  content?: string | null
}

export const SearchCatalogAvailabilityFilter = {
  Available: "available",
  Ignore: "ignore",
} as const

export const SearchCatalogEntityIndexingMode = {
  Product: "product",
  Variant: "variant",
} as const

export const SearchCatalogUrlType = {
  Product: "product",
  Checkout: "checkout",
} as const

export const SearchCatalogVisibilityMode = {
  All: "all",
  Allow: "allow",
  Deny: "deny",
} as const

// Collections are referenced by handle because Shopify collection titles are not unique.
const SearchCatalogProductSelectorSchema = z.object({
  tags: z.array(z.string()).default([]),
  collections: z.array(z.string()).default([]),
})

export type SearchCatalogProductSelector = z.infer<typeof SearchCatalogProductSelectorSchema>

const SearchCatalogVisibilitySchema = SearchCatalogProductSelectorSchema.extend({
  mode: z.enum(SearchCatalogVisibilityMode).default(SearchCatalogVisibilityMode.All),
})

export type SearchCatalogVisibility = z.infer<typeof SearchCatalogVisibilitySchema>

export const SearchCatalogBoostEntryType = {
  Tag: "tag",
  Collection: "collection",
} as const

export const SEARCH_CATALOG_MAX_BOOST_ENTRIES = 5

// Ordered by priority: earlier entries get a stronger boost.
const SearchCatalogBoostSchema = z
  .array(z.object({ type: z.enum(SearchCatalogBoostEntryType), value: z.string() }))
  .max(SEARCH_CATALOG_MAX_BOOST_ENTRIES)

export type SearchCatalogBoost = z.infer<typeof SearchCatalogBoostSchema>

export const StockMode = { Ignore: "ignore", Available: "available", Strict: "strict" } as const
export const LocationStrategy = {
  Aggregate: "aggregate",
  OnlineOnly: "online_only",
  Specific: "specific",
} as const

export const SearchCatalogStockConfigSchema = z
  .object({
    stockMode: z.enum(StockMode).default(StockMode.Ignore),
    locationStrategy: z.enum(LocationStrategy).default(LocationStrategy.OnlineOnly),
    specificLocationIds: z.array(z.string()).default([]),
    allowBackorder: z.boolean().default(true),
    minQuantity: z.number().int().min(1).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.locationStrategy === "specific" && data.specificLocationIds.length === 0) {
      ctx.addIssue({
        code: "custom",
        message: Errors.FIELD_REQUIRED,
        path: ["specificLocationIds"],
      })
    }
  })

export type SearchCatalogStockConfig = z.infer<typeof SearchCatalogStockConfigSchema>

export const SearchCatalogCapabilityConfigSchema = z.object({
  provider: z.enum(SearchCatalogProvider),
  lastSyncedAt: z.string().nullish(),
  productsCount: z.number().int().nullish(),
  lastSyncedEntityIndexingMode: z.enum(SearchCatalogEntityIndexingMode).nullish(),
  lastSyncedGroupVariantsBy: z.string().nullish(),
  groupVariantsBy: z.string().trim().min(1).nullish(),
  availableVariantOptions: z.array(z.string()).nullish(),
  availableTags: z.array(z.string()).nullish(),
  availableCollections: z.array(z.object({ handle: z.string(), title: z.string() })).nullish(),
  utmParams: SearchCatalogUtmParamsSchema.optional(),
  entityIndexingMode: z
    .enum(SearchCatalogEntityIndexingMode)
    .default(SearchCatalogEntityIndexingMode.Product),
  availabilityFilter: z
    .enum(SearchCatalogAvailabilityFilter)
    .default(SearchCatalogAvailabilityFilter.Ignore),
  urlType: z.enum(SearchCatalogUrlType).default(SearchCatalogUrlType.Checkout),
  visibility: SearchCatalogVisibilitySchema.optional(),
  boost: SearchCatalogBoostSchema.optional(),
})

export const ProductInformationCapabilityConfigSchema = z.object({
  provider: z.enum(SearchCatalogProvider).default(SearchCatalogProvider.Shopify),
  stockConfig: SearchCatalogStockConfigSchema.optional(),
})

// Instruction configuration
export const InstructionCapabilityConfigSchema = z.object({
  name: z.string({ message: Errors.FIELD_REQUIRED }).min(1, { message: Errors.FIELD_REQUIRED }),
  activationPrompt: z
    .string({ message: Errors.FIELD_REQUIRED })
    .min(1, { message: Errors.FIELD_REQUIRED })
    .max(4096, { message: Errors.FIELD_MAX_LENGTH }),
  dataToExtract: z.array(
    z.object({
      name: z.string({ message: Errors.FIELD_REQUIRED }).min(1, { message: Errors.FIELD_REQUIRED }),
      type: JsonSchemaTypeSchema,
      description: z.string().optional(),
      required: z.boolean().default(true),
    }),
  ),
  agentPrompt: z
    .string({ message: Errors.FIELD_REQUIRED })
    .min(1, { message: Errors.FIELD_REQUIRED })
    .max(4096, { message: Errors.FIELD_MAX_LENGTH }),
  executeOnlyOnce: z.boolean().default(false),
})

export type InstructionCapabilityConfig = z.infer<typeof InstructionCapabilityConfigSchema>

export { WebhookBodyFormat } from "lopermut"

export const WebhookMethod = {
  GET: "GET",
  POST: "POST",
  PUT: "PUT",
  PATCH: "PATCH",
  DELETE: "DELETE",
} as const

// Kept for backward-compatibility with old stored configs (migration only)
export const WebhookBodyFieldType = {
  string: "string",
  number: "number",
  boolean: "boolean",
} as const

export const WebhookUrlSchema = z
  .string({ message: Errors.FIELD_REQUIRED })
  .url({ message: Errors.INVALID_URL })
  .refine(
    (url) => {
      try {
        validateWebhookUrl(url)
        return true
      } catch {
        return false
      }
    },
    { message: Errors.INVALID_URL },
  )

const webhookHeadersSchema = z.array(
  z.object({
    name: z.string({ message: Errors.FIELD_REQUIRED }).min(1, { message: Errors.FIELD_REQUIRED }),
    value: z.string({ message: Errors.FIELD_REQUIRED }).min(1, { message: Errors.FIELD_REQUIRED }),
  }),
)

// Accepts either a JSON string (new format) or the deprecated key-value array,
// and always transforms to a JSON string.
const webhookBodySchema = z
  .union([
    z.string(),
    z.array(
      z.object({
        name: z
          .string({ message: Errors.FIELD_REQUIRED })
          .min(1, { message: Errors.FIELD_REQUIRED }),
        value: z
          .string({ message: Errors.FIELD_REQUIRED })
          .min(1, { message: Errors.FIELD_REQUIRED }),
        type: z.enum(WebhookBodyFieldType).optional(),
      }),
    ),
  ])
  .transform((value) => {
    if (typeof value === "string") return value
    if (value.length === 0) return "{}"
    const lines = value.map((field) => {
      const escapedName = JSON.stringify(field.name)
      if (field.type === "number" || field.type === "boolean") {
        return `  ${escapedName}: ${field.value}`
      }
      return `  ${escapedName}: ${JSON.stringify(field.value)}`
    })
    return `{\n${lines.join(",\n")}\n}`
  })
  .default("{}")

export const WebhookStepSchema = z.object({
  url: WebhookUrlSchema,
  method: z.enum(WebhookMethod).default(WebhookMethod.POST),
  webhookHeaders: webhookHeadersSchema,
  webhookBody: webhookBodySchema,
  webhookBodyFormat: z.enum(WebhookBodyFormat).default(WebhookBodyFormat.json),
  testResponseBody: z.string().nullish(),
})

export type WebhookStep = z.infer<typeof WebhookStepSchema>

export const WebhookCapabilityConfigBaseSchema = InstructionCapabilityConfigSchema.extend({
  url: WebhookUrlSchema.optional(),
  method: z.enum(WebhookMethod).default(WebhookMethod.POST),
  webhookHeaders: webhookHeadersSchema,
  webhookBody: webhookBodySchema,
  webhookBodyFormat: z.enum(WebhookBodyFormat).default(WebhookBodyFormat.json),
  testResponseBody: z.string().nullish(),
  useCache: z.boolean().default(true),
  mode: z.enum(["single", "chain"]).default("single"),
  steps: z.array(WebhookStepSchema).default([]),
})

export const WebhookCapabilityConfigSchema = WebhookCapabilityConfigBaseSchema.superRefine(
  (config, ctx) => {
    if (config.mode === "chain") {
      if (config.steps.length < 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: Errors.FIELD_REQUIRED,
          path: ["steps"],
        })
      }
      return
    }

    if (!config.url) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: Errors.FIELD_REQUIRED,
        path: ["url"],
      })
    }
  },
)

export type WebhookCapabilityConfig = z.infer<typeof WebhookCapabilityConfigSchema>

// MCP configuration
export const McpAuthType = {
  None: "none",
  Bearer: "bearer",
  ApiKey: "apiKey",
  Custom: "custom",
} as const

export const McpTransportType = {
  SSE: "sse",
  StreamableHTTP: "streamable-http",
} as const

export const McpCapabilityConfigSchema = z.object({
  name: z.string({ message: Errors.FIELD_REQUIRED }).min(1, { message: Errors.FIELD_REQUIRED }),
  transportType: z
    .enum(["sse", "streamable-http"], { message: Errors.FIELD_REQUIRED })
    .default("sse"),
  serverUrl: z
    .string({ message: Errors.FIELD_REQUIRED })
    .url({ message: Errors.INVALID_URL })
    .refine(
      (url) => {
        try {
          validateWebhookUrl(url)
          return true
        } catch {
          return false
        }
      },
      { message: Errors.INVALID_URL },
    ),
  authConfig: z
    .object({
      type: z.enum(McpAuthType),
      token: z.string().optional(),
      apiKey: z.string().optional(),
      headerName: z.string().optional(),
      customHeaders: z.record(z.string(), z.string()).optional(),
    })
    .optional(),
  tools: z
    .array(
      z.object({
        name: z.string(),
        description: z.string().optional(),
        inputSchema: z.record(z.string(), z.unknown()).optional(),
        enabled: z.boolean(),
      }),
    )
    .refine((tools) => tools.some((t) => t.enabled), {
      message: Errors.FIELD_REQUIRED,
    }),
  connectionTimeoutMs: z
    .union([z.string(), z.number()])
    .refine(
      (val) => {
        const parsed = typeof val === "number" ? val : parseInt(val, 10)
        return !isNaN(parsed) && parsed > 0
      },
      { message: Errors.INVALID_NUMBER },
    )
    .transform((val) => (typeof val === "number" ? val : parseInt(val, 10))),
  executionTimeoutMs: z
    .union([z.string(), z.number()])
    .refine(
      (val) => {
        const parsed = typeof val === "number" ? val : parseInt(val, 10)
        return !isNaN(parsed) && parsed > 0
      },
      { message: Errors.INVALID_NUMBER },
    )
    .transform((val) => (typeof val === "number" ? val : parseInt(val, 10))),
  toolsCachedAt: z.string().optional(),
})

export type McpCapabilityConfig = z.infer<typeof McpCapabilityConfigSchema>

// Request review configuration
export const ReviewProviderType = {
  GoogleMaps: "google_maps",
  Trustpilot: "trustpilot",
} as const

export const ReviewSelectionStrategy = {
  Auto: "auto",
  ForceProvider: "force_provider",
} as const

export const ReviewScope = {
  Venue: "venue",
  Global: "global",
} as const

const ReviewGoogleMapsProviderSchema = z.object({
  id: z
    .string()
    .uuid()
    .default(() => crypto.randomUUID()),
  type: z.literal("google_maps"),
  scope: z.literal("venue").default("venue"),
  label: z.string().default(""),
  link: z.string().url({ message: Errors.INVALID_URL }),
})

const ReviewTrustpilotProviderSchema = z.object({
  id: z
    .string()
    .uuid()
    .default(() => crypto.randomUUID()),
  type: z.literal("trustpilot"),
  scope: z.literal("global").default("global"),
  link: z.string().url({ message: Errors.INVALID_URL }),
})

export const ReviewProviderSchema = z.discriminatedUnion("type", [
  ReviewGoogleMapsProviderSchema,
  ReviewTrustpilotProviderSchema,
])

export type ReviewProvider = z.infer<typeof ReviewProviderSchema>

export const RequestReviewCapabilityConfigSchema = z
  .object({
    providers: z.array(ReviewProviderSchema).default([]),
    definition: z.string().max(1536, { message: Errors.FIELD_MAX_LENGTH }).default(""),
    selection: z
      .object({
        strategy: z.enum(ReviewSelectionStrategy).default("auto"),
        forcedProviderId: z.string().uuid().optional(),
        forcedProviderType: z.string().optional(), // legacy — kept so old configs parse, ignored at runtime
      })
      .default({ strategy: "auto" }),
    isDisabled: z.boolean().optional(),
    isWorkflowOnly: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.selection.strategy === "force_provider") {
      if (!data.selection.forcedProviderId) {
        ctx.addIssue({
          code: "custom",
          message: Errors.FIELD_REQUIRED,
          path: ["selection", "forcedProviderId"],
        })
      } else if (!data.providers.some((p) => p.id === data.selection.forcedProviderId)) {
        ctx.addIssue({
          code: "custom",
          message: Errors.CAPABILITY_CONFIG_INVALID,
          path: ["selection", "forcedProviderId"],
        })
      }
    }
  })

export type RequestReviewCapabilityConfig = z.infer<typeof RequestReviewCapabilityConfigSchema>
